<template>
    <div class="earnings">
        <div class="earnings__header">{{ t('activity-tracker.tooltip.earnings.title') }}</div>
        <div>
            <div class="earnings__row">
                <div class="earnings__row-label">{{ t('activity-tracker.tooltip.total.excl.taxes') }}</div>
                <div class="earnings__row-amount">{{ formatAmount(currency!, totalAmountExcludingTaxes!, {decimals: 2}) }}</div>
            </div>
            <div v-for="tax in taxesValues" :key="tax.name" class="earnings__row">
                <div class="earnings__row-label">{{ tax.name }}</div>
                <div class="earnings__row-amount">{{ `${tax.rate}%` }}</div>
            </div>
            <div class="earnings__row">
                <div class="earnings__row-label">{{ t('activity-tracker.tooltip.total.incl.taxes') }}</div>
                <div class="earnings__row-amount">{{ formatAmount(currency!, totalAmountIncludingTaxes!, {decimals: 2}) }}</div>
            </div>
            <div class="earnings__row">
                <div class="earnings__row-label">
                    {{ t('activity-tracker.tooltip.malt.fees.excl.taxes', {0: freelancerFeesPercentageValue}) }}
                </div>
                <div class="earnings__row-amount">{{ formatAmount(currency!, freelanceFeesExcludingTaxes!, {decimals: 2}) }}</div>
            </div>
            <div class="earnings__row">
                <div class="earnings__row-label">{{ t('activity-tracker.tooltip.malt.fees.incl.taxes') }}</div>
                <div class="earnings__row-amount">{{ formatAmount(currency!, freelanceFeesIncludingTaxes!, {decimals: 2}) }}</div>
            </div>
        </div>
        <div class="earnings__divider"></div>

        <div v-if="!totalAmountInFreelancerCurrency" class="earnings__info">
            <div v-html="t('activity-tracker.tooltip.received.incl.taxes')"></div>
            <div class="earnings__info-amount">{{ formatAmount(currency!, amountExcludingServiceFeesIncludingTaxes!, {decimals: 2}) }}</div>
        </div>

        <div v-else>
            <div class="earnings__multi-currency-info">
                <div v-html="t('activity-tracker.tooltip.multi.currency.invoiced', {0: getCurrencySymbolFrom(currency!)})" />
                <div class="earnings__multi-currency-info-amount">
                    {{ formatAmount(currency!, amountExcludingServiceFeesIncludingTaxes!, {decimals: 2}) }}
                </div>
            </div>

            <div class="earnings__multi-currency-highlight">
                <div class="earnings__multi-currency-highlight-info">
                    <div v-html="t('activity-tracker.tooltip.multi.currency.approximately.received')"></div>
                    <div class="earnings__multi-currency-highlight-amount">
                        ≈ {{ formatAmount(freelancerCurrency!, totalAmountInFreelancerCurrency!, {decimals: 2}) }}
                    </div>
                </div>
                <div
                    v-html="t('activity-tracker.multi.currency.earnings.info', {0: formatAmount(currency!, estimationExchangeRate!, {decimals: 2})})"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {useTranslation, useLocale, useFormat} from '#imports';
    import {useEarningsOfTheMonthStore} from '@/common/stores';

    const {t} = useTranslation();
    const {formatAmount, getCurrencySymbolFrom} = useFormat();

    const earningsOfTheMonthStore = useEarningsOfTheMonthStore();
    const {
        amountExcludingServiceFeesIncludingTaxes,
        freelanceFeesExcludingTaxes,
        freelanceFeesIncludingTaxes,
        totalAmountExcludingTaxes,
        totalAmountIncludingTaxes,
        currency,
        freelancerFeesPercentageValue,
        freelancerCurrency,
        totalAmountInFreelancerCurrency,
        taxesValues,
        estimationExchangeRate,
    } = storeToRefs(earningsOfTheMonthStore);
</script>

<style lang="scss" scoped>
    @mixin earnings-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: var(--joy-color-secondary-50);
        font-weight: var(--joy-font-weight-bold);
        font-size: var(--joy-font-size-primary-300);
    }
    .earnings {
        &__header {
            margin-bottom: var(--joy-core-spacing-5);
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-400);
        }

        &__row {
            font-size: var(--joy-font-size-primary-300);
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: var(--joy-core-spacing-3);
            gap: var(--joy-core-spacing-8);
            margin-right: var(--joy-core-spacing-5);

            &-amount {
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-400);
            }
            &-label {
                width: max-content;
            }
        }

        &__divider {
            border: 1px solid var(--joy-color-neutral-30);
            width: 100%;
            margin: var(--joy-core-spacing-3) 0px;
        }

        &__info {
            @include earnings-info;
            background-color: var(--joy-color-information-10);
            padding: var(--joy-core-spacing-4);
            border-radius: var(--joy-core-radius-2);

            &-amount {
                font-size: var(--joy-font-size-primary-400);
            }
        }
        &__multi-currency-info {
            @include earnings-info;
            margin: var(--joy-core-spacing-6) 0px;

            &-amount {
                font-size: var(--joy-font-size-primary-400);
                margin-right: var(--joy-core-spacing-5);
            }
        }

        &__multi-currency-highlight {
            background-color: var(--joy-color-information-10);
            padding: var(--joy-core-spacing-4);
            border-radius: var(--joy-core-radius-2);

            &-info {
                margin-bottom: var(--joy-core-spacing-3);
                @include earnings-info;
            }

            &-amount {
                font-size: var(--joy-font-size-primary-400);
            }
        }
    }
</style>
